import { useState } from 'react';
import type BackendService from '../../../services/admin/BackendService';
import { readClipboardPayload } from './clipboardPayload';
import { useCropAdjust } from './useCropAdjust';

function errText(err: unknown): string {
  return (
    (err as { error?: string; message?: string })?.error ??
    (err as Error)?.message ??
    'Could not read that screenshot.'
  );
}

/** Create a throwaway file input, open it, and hand back the chosen image / PDF. */
function openImagePicker(onFile: (file: File) => void): void {
  const input = document.createElement('input');
  input.type = 'file';
  input.accept = 'image/*,application/pdf';
  input.onchange = () => {
    const file = input.files?.[0];
    if (file) onFile(file);
  };
  input.click();
}

/** The explicit-button entry point: use a clipboard image if present, else pick a file. */
function startFromClipboard(run: (file: File) => void, setError: (e: string | null) => void): void {
  setError(null);
  readClipboardPayload()
    .then((payload) => (payload.image ? run(payload.image) : openImagePicker((f) => run(f))))
    .catch(() => openImagePicker((f) => run(f)));
}

export interface ImageCardsSplitState<P> {
  busy: boolean;
  error: string | null;
  /** The post-split "these are cropped, drop a hi-res original to replace" hint. */
  showNote: boolean;
  dismissNote: () => void;
  /** Non-null when the block already had slides and a split is awaiting Replace. */
  pending: P | null;
  confirmPending: () => void;
  cancelPending: () => void;
  /** The split result awaiting the user's crop review (low-confidence pass). */
  review: P | null;
  reviewOpen: boolean;
  /** Apply the reviewed result (the dialog passes it back, cards it dropped removed). */
  applyReview: (props: P) => void;
  cancelReview: () => void;
  /** Explicit "Split a screenshot into cards" button — clipboard image or picker. */
  start: () => void;
  /** A dropped/pasted image file — auto-detects a multi-card layout. */
  offerOrAdd: (file: File) => void;
  offerCount: number;
  offerOpen: boolean;
  acceptOffer: () => void;
  declineOffer: () => void;
  cancelOffer: () => void;
  /** The last split's source + boxes are kept so the user can re-crop. */
  canAdjust: boolean;
  adjustOpen: boolean;
  adjustSource: AdjustSource | null;
  adjustBoxes: AdjustBox[];
  adjustHeadings: string[];
  openAdjust: () => void;
  cancelAdjust: () => void;
  applyAdjust: (boxes: AdjustBox[]) => void;
  /** Adopt a cards result detected elsewhere (e.g. a document-library import)
   *  as if it had come from a direct paste — same review/adjust treatment. */
  adoptDetected: (detected: { properties: P } & ImageCardsCropMeta) => void;
  /** Like `adoptDetected`, but the properties are already applied — only
   *  remember the source/boxes and surface the "Adjust crops" note. */
  primeAdjust: (properties: P, source: AdjustSource, boxes: AdjustBox[]) => void;
}

export type AdjustBox = { left: number; top: number; width: number; height: number };
export type AdjustSource = { url: string; path: string; width: number; height: number };

/** The crop metadata any "detected N cards" result carries, however it was produced. */
export interface ImageCardsCropMeta {
  source: AdjustSource | null;
  boxes: AdjustBox[];
  confidence: string;
}

/**
 * The auto-detect layer: on any image pasted into the Image Cards block, ask the
 * backend whether it is a row/grid of captioned photos and, if so, offer a split
 * instead of silently adding it as one slide.
 */
function useMultiCardOffer(opts: {
  backendService?: BackendService;
  onAddSlide: (file: File) => void;
  onSplit: (file: File) => void;
}) {
  const { backendService, onAddSlide, onSplit } = opts;
  const [detecting, setDetecting] = useState(false);
  const [offer, setOffer] = useState<{ file: File; count: number } | null>(null);

  const offerOrAdd = (file: File) => {
    if (!backendService?.detectImageCards) {
      onAddSlide(file);
      return;
    }
    setDetecting(true);
    backendService
      .detectImageCards(file)
      .then(({ cardCount }: { cardCount: number }) =>
        cardCount >= 2 ? setOffer({ file, count: cardCount }) : onAddSlide(file),
      )
      .catch(() => onAddSlide(file))
      .finally(() => setDetecting(false));
  };

  return {
    detecting,
    offerOrAdd,
    offerCount: offer?.count ?? 0,
    offerOpen: offer !== null,
    acceptOffer: () => {
      if (!offer) return;
      const { file } = offer;
      setOffer(null);
      onSplit(file);
    },
    declineOffer: () => {
      if (offer) onAddSlide(offer.file);
      setOffer(null);
    },
    cancelOffer: () => setOffer(null),
  };
}

/**
 * State + flow for turning a screenshot into image cards: `start()` (the
 * explicit button — clipboard image or picker) and `offerOrAdd()` (every image
 * pasted into the block — see {@link useMultiCardOffer}). A Replace confirm
 * guards a block that already has slides; a one-off note flags low-res crops.
 */
/** Low-confidence splits pause for a crop review before they land. Returns the
 *  state to spread into the hook result, plus a `present(props)` opener. */
function useSplitReview<P>(route: (props: P) => void) {
  const [review, setReview] = useState<P | null>(null);
  const api = {
    review,
    reviewOpen: review !== null,
    applyReview: (props: P) => {
      setReview(null);
      route(props);
    },
    cancelReview: () => setReview(null),
  };
  return [api, (props: P) => setReview(props)] as const;
}

/** Remember the source/boxes for Adjust crops, then route to review or straight in,
 *  exactly the same way regardless of whether the cards came from a paste or a
 *  document-library import. */
function routeDetectedCards<P>(
  detected: { properties: P } & ImageCardsCropMeta,
  rememberAdjust: (props: P, source: AdjustSource | null, boxes: AdjustBox[]) => void,
  presentReview: (props: P) => void,
  route: (props: P) => void,
) {
  rememberAdjust(detected.properties, detected.source ?? null, detected.boxes ?? []);
  if (detected.confidence !== 'high') presentReview(detected.properties);
  else route(detected.properties);
}

async function extractSplit<P>(
  backendService: BackendService,
  modelType: string,
  modelId: string,
  file: File,
  rememberAdjust: (props: P, source: AdjustSource | null, boxes: AdjustBox[]) => void,
  presentReview: (props: P) => void,
  route: (props: P) => void,
) {
  const { properties, confidence, source, boxes } =
    await backendService.extractImageCardsFromScreenshot(modelType, modelId, file);
  routeDetectedCards(
    { properties: properties as P, confidence, source: source ?? null, boxes: boxes ?? [] },
    rememberAdjust,
    presentReview,
    route,
  );
}

async function runSplit<P>(opts: {
  backendService?: BackendService;
  modelType: string;
  modelId: string;
  file: File;
  rememberAdjust: (props: P, source: AdjustSource | null, boxes: AdjustBox[]) => void;
  presentReview: (props: P) => void;
  route: (props: P) => void;
  setBusy: (busy: boolean) => void;
  setError: (error: string | null) => void;
  setShowNote: (show: boolean) => void;
}) {
  if (!opts.backendService) {
    opts.setError('Splitting a screenshot needs a backend connection.');
    return;
  }
  opts.setBusy(true);
  opts.setError(null);
  opts.setShowNote(false);
  try {
    await extractSplit(
      opts.backendService,
      opts.modelType,
      opts.modelId,
      opts.file,
      opts.rememberAdjust,
      opts.presentReview,
      opts.route,
    );
  } catch (err) {
    opts.setError(errText(err));
  } finally {
    opts.setBusy(false);
  }
}

function useSplitSession<P>(opts: {
  hasSlides: boolean;
  modelId: string;
  modelType: string;
  contentBlockId?: string;
  backendService?: BackendService;
  onApply: (props: P) => void;
}) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showNote, setShowNote] = useState(false);
  const [pending, setPending] = useState<P | null>(null);

  const apply = (next: P) => {
    opts.onApply(next);
    setPending(null);
    setShowNote(true);
  };
  const route = (next: P) => (opts.hasSlides ? setPending(next) : apply(next));
  const [reviewApi, presentReview] = useSplitReview<P>(route);
  const [adjustApi, rememberAdjust] = useCropAdjust<P>({
    modelId: opts.modelId,
    modelType: opts.modelType,
    contentBlockId: opts.contentBlockId,
    backendService: opts.backendService,
    apply,
    setBusy,
    setError,
  });

  const run = (file: File) =>
    runSplit({
      backendService: opts.backendService,
      modelType: opts.modelType,
      modelId: opts.modelId,
      file,
      rememberAdjust,
      presentReview,
      route,
      setBusy,
      setError,
      setShowNote,
    });

  /** Adopt a cards result detected elsewhere (e.g. a document-library import)
   *  as if it had come from a direct paste — same review/adjust treatment. */
  const adopt = (detected: { properties: P } & ImageCardsCropMeta) => {
    setShowNote(false);
    routeDetectedCards(detected, rememberAdjust, presentReview, route);
  };

  /** Like `adopt`, but the properties are already applied (an unscoped library
   *  import appended this block directly) — only remember the source/boxes
   *  and surface the note, without re-applying or re-running crop review. */
  const prime = (properties: P, source: AdjustSource, boxes: AdjustBox[]) => {
    rememberAdjust(properties, source, boxes, { persist: false });
    setShowNote(true);
  };

  return {
    busy,
    error,
    setError,
    showNote,
    setShowNote,
    pending,
    setPending,
    apply,
    run,
    adopt,
    prime,
    reviewApi,
    adjustApi,
  };
}

export function useImageCardsSplit<P>(opts: {
  hasSlides: boolean;
  modelId: string;
  modelType?: string;
  /** The content block this split lives in — lets Adjust crops survive a
   *  reload (persisted keyed by this id). Omit for a block not yet saved
   *  anywhere a reload could find it again. */
  contentBlockId?: string;
  backendService?: BackendService;
  onApply: (props: P) => void;
  onAddSlide: (file: File) => void;
}): ImageCardsSplitState<P> {
  const { modelType = 'FundOpportunity', backendService, onAddSlide } = opts;
  const session = useSplitSession({
    hasSlides: opts.hasSlides,
    modelId: opts.modelId,
    modelType,
    contentBlockId: opts.contentBlockId,
    backendService,
    onApply: opts.onApply,
  });
  const { detecting, ...offerApi } = useMultiCardOffer({
    backendService,
    onAddSlide,
    onSplit: (f) => void session.run(f),
  });

  return {
    busy: session.busy || detecting,
    error: session.error,
    showNote: session.showNote,
    dismissNote: () => session.setShowNote(false),
    pending: session.pending,
    confirmPending: () => session.pending && session.apply(session.pending),
    cancelPending: () => session.setPending(null),
    ...session.reviewApi,
    ...session.adjustApi,
    ...offerApi,
    start: () => startFromClipboard((f) => void session.run(f), session.setError),
    adoptDetected: session.adopt,
    primeAdjust: session.prime,
  };
}
